var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { ACTIVE_TARIFFS, cashbackLookup, defaultAddonsLookup, defaultDeductiblesLookup, EMPLOYMENT_STATUSES, hiMedicalTariffKind, } from './models';
import { tariffNameMapping, tariffLongNameMapping } from './models/tariff';
import { addOnNameMapping, hiDentalAddOnKind } from './models/addOn';
import { priceForTariff } from './pricing/tariff';
import { computeStatutorySurcharge } from './pricing/statutorySurcharge';
import { computeLongTermCare } from './pricing/longTermCare';
import { addOnPricing, jokerFlexAddon, nkSFlexAddon } from './pricing/addOn';
import { getAgeFromBirthYear, getDefaultSickDayPayout, getTariffLabel, isOver50, roundToTwoDecimal, } from './util';
export { ACTIVE_TARIFFS, addOnNameMapping, defaultAddonsLookup, EMPLOYMENT_STATUSES, getAgeFromBirthYear, getDefaultSickDayPayout, getTariffLabel, hiDentalAddOnKind, isOver50, tariffLongNameMapping, tariffNameMapping, };
// 01.01.2025
var MAX_EMPLOYER_CONTRIBUTION = 471.32;
var MAX_EMPLOYER_LONG_TERM_CARE = 99.32;
export var pricesForTariff = function (_a) {
    var _b;
    var birthYear = _a.birthYear, tariff = _a.tariff, deductible = _a.deductible, _c = _a.selectedAddOns, selectedAddOns = _c === void 0 ? [] : _c, _d = _a.riskFactor, riskFactor = _d === void 0 ? 0 : _d, sickDayPayout = _a.sickDayPayout, excludeLongTermCare = _a.excludeLongTermCare;
    var age = getAgeFromBirthYear(birthYear);
    var tariffPrice = roundToTwoDecimal(priceForTariff(age, tariff, deductible));
    var statutorySurcharge = roundToTwoDecimal(computeStatutorySurcharge(age, tariff) * tariffPrice);
    var addOns = selectedAddOns
        .filter(function (a) {
        // We remove joker flex add on if age is out of bound (i.e > 45 y.o.)
        if (a === 'FLEX') {
            return jokerFlexAddon(age) !== 0;
        }
        // We remove nksflex add on if age is out of bound (i.e > 66 y.o.)
        if (a === 'NKSFLEX') {
            return nkSFlexAddon(age) !== 0;
        }
        // We remove any hi dental add on kind if selected tariff isn’t of kind hi medical
        if (hiMedicalTariffKind.includes(tariff) === false &&
            hiDentalAddOnKind.includes(a) === true) {
            return undefined;
        }
        // We only keep hi dental add on if selected both hi dental and hi dental plus add on
        if (hiMedicalTariffKind.includes(tariff) === true &&
            selectedAddOns.includes('HD') &&
            selectedAddOns.includes('HDL') &&
            a === 'HD') {
            return undefined;
        }
        return true;
    })
        .reduce(function (a, b) {
        var _a;
        return (__assign(__assign({}, a), (_a = {}, _a[b] = roundToTwoDecimal(addOnPricing[b](age, sickDayPayout)), _a)));
    }, {});
    var longTermCare = excludeLongTermCare
        ? 0
        : roundToTwoDecimal(computeLongTermCare(age));
    var addOnsPrice = roundToTwoDecimal((_b = Object.values(addOns).reduce(function (a, b) { return a + b; }, 0)) !== null && _b !== void 0 ? _b : 0);
    var riskSurcharge = roundToTwoDecimal(riskFactor * tariffPrice);
    var totalPrice = roundToTwoDecimal(tariffPrice +
        statutorySurcharge +
        riskSurcharge +
        addOnsPrice +
        longTermCare);
    return {
        tariffPrice: tariffPrice,
        statutorySurcharge: statutorySurcharge,
        riskSurcharge: riskSurcharge,
        longTermCare: longTermCare,
        addOns: addOns,
        totalPrice: totalPrice,
    };
};
export var contributionsForTariff = function (options) {
    var get = function (number) { return number !== null && number !== void 0 ? number : 0; };
    var _a = options.tariff, tariffPrice = _a.tariffPrice, statutorySurcharge = _a.statutorySurcharge, riskSurcharge = _a.riskSurcharge, longTermCare = _a.longTermCare, addOns = _a.addOns;
    var currentAddons = Object.keys(addOns);
    var totalAddons = currentAddons
        .filter(function (addon) { return addon !== 'URZ'; })
        .reduce(function (acc, key) { return acc + get(addOns[key]); }, 0);
    var tariffWithSurcharges = tariffPrice + statutorySurcharge + riskSurcharge + totalAddons;
    var baseOwnContributions = tariffWithSurcharges +
        /*
          The employer does not cover this next Addon,
          so we have to add it only to the ownContribution value
        */
        get(addOns.URZ) +
        longTermCare;
    if (options.employmentStatus !== 'EMPLOYED_IN_GERMANY') {
        return {
            tariffPrice: Number(tariffPrice.toFixed(2)),
            tariffLongTermCare: Number(longTermCare.toFixed(2)),
            employerContributions: null,
            employerLongTermCare: null,
            ownContributions: Number(baseOwnContributions.toFixed(2)),
            addOns: addOns,
        };
    }
    var employerBaseContributions = tariffWithSurcharges / 2;
    var employerLongTermCare = longTermCare / 2;
    if (employerBaseContributions > MAX_EMPLOYER_CONTRIBUTION) {
        employerBaseContributions = MAX_EMPLOYER_CONTRIBUTION;
    }
    if (employerLongTermCare > MAX_EMPLOYER_LONG_TERM_CARE) {
        employerLongTermCare = MAX_EMPLOYER_LONG_TERM_CARE;
    }
    var employerContributions = employerBaseContributions + employerLongTermCare;
    var ownContributions = baseOwnContributions - employerContributions;
    var unusedBaseEmployerContributions = MAX_EMPLOYER_CONTRIBUTION - employerBaseContributions;
    var unusedLongTermCareEmployerContributions = MAX_EMPLOYER_LONG_TERM_CARE - employerLongTermCare;
    return {
        tariffPrice: Number(tariffPrice.toFixed(2)),
        tariffLongTermCare: Number(longTermCare.toFixed(2)),
        employerContributions: Number(employerContributions.toFixed(2)),
        employerLongTermCare: Number(employerLongTermCare.toFixed(2)),
        ownContributions: Number(ownContributions.toFixed(2)),
        unusedEmployerContributions: {
            base: parseFloat(unusedBaseEmployerContributions.toFixed(2)),
            longTermCare: parseFloat(unusedLongTermCareEmployerContributions.toFixed(2)),
        },
        addOns: addOns,
    };
};
var cashbackForTariff = function (_a) {
    var tariff = _a.tariff, tariffBasePrice = _a.tariffBasePrice, _b = _a.isOverTwentyYears, isOverTwentyYears = _b === void 0 ? true : _b, _c = _a.deductible, deductible = _c === void 0 ? '0' : _c;
    var guaranteedMonthlyCashback = 
    // Monthly cashback only exists for deductible "0"
    deductible === '0'
        ? // Children under 21 receive only half of the guaranteed cashback
            isOverTwentyYears
                ? cashbackLookup[tariff]
                : cashbackLookup[tariff] / 2
        : 0;
    var guaranteedYearlyCashback = guaranteedMonthlyCashback * 12;
    var yearlyNoClaimsBonusFactors = [1, 1.25, 1.5, 1.75, 2, 2.25];
    var getNoClaimsInAYearBonus = function (factor) {
        return Math.round(tariffBasePrice * factor);
    };
    var yearlyNoClaimsBonuses = yearlyNoClaimsBonusFactors.map(function (value) { return getNoClaimsInAYearBonus(value); });
    var firstYearNoClaimsBonus = yearlyNoClaimsBonuses[0];
    var totalPotentialCashback = guaranteedYearlyCashback + firstYearNoClaimsBonus;
    return {
        guaranteedMonthlyCashback: guaranteedMonthlyCashback,
        firstYearNoClaimsBonus: firstYearNoClaimsBonus,
        yearlyNoClaimsBonuses: yearlyNoClaimsBonuses,
        totalPotentialCashback: totalPotentialCashback,
    };
};
/**
 * @description Gives you a price breakdown of the tariff, its add-ons, the contributions (and cashback, if available)
 *
 * Provide either a specific {@link sickDayPayout} or an {@link params|income} to calculate a default sickday payout from
 *
 */
export var getPriceBreakdown = function (params) {
    var tariff = params.tariff, birthYear = params.birthYear, _a = params.employmentStatus, employmentStatus = _a === void 0 ? 'EMPLOYED_OUTSIDE_OF_GERMANY' : _a, selectedAddOns = params.selectedAddOns, riskFactor = params.riskFactor, deductibleOverride = params.deductible, includeUnusedEmployerContributions = params.includeUnusedEmployerContributions, excludeLongTermCare = params.excludeLongTermCare;
    var addOns = selectedAddOns || defaultAddonsLookup[tariff];
    var deductible = deductibleOverride !== null && deductibleOverride !== void 0 ? deductibleOverride : defaultDeductiblesLookup[tariff];
    var sickDayPayout;
    if ('income' in params) {
        sickDayPayout = getDefaultSickDayPayout(params.income);
    }
    else {
        sickDayPayout = params.sickDayPayout;
    }
    var tariffPriceInfo = pricesForTariff({
        birthYear: birthYear,
        tariff: tariff,
        deductible: deductible,
        selectedAddOns: addOns,
        sickDayPayout: sickDayPayout,
        riskFactor: riskFactor,
        excludeLongTermCare: excludeLongTermCare,
    });
    var tariffContributions = contributionsForTariff({
        tariff: tariffPriceInfo,
        employmentStatus: employmentStatus,
    });
    var addOnsTotalPrice = Object.values(tariffPriceInfo.addOns).reduce(function (sum, addOnPrice) { return sum + addOnPrice; }, 0);
    var basePrice = tariffPriceInfo.tariffPrice, riskSurcharge = tariffPriceInfo.riskSurcharge, longTermCare = tariffPriceInfo.longTermCare, statutorySurcharge = tariffPriceInfo.statutorySurcharge, totalPrice = tariffPriceInfo.totalPrice, pricePerAddOn = tariffPriceInfo.addOns;
    var tariffsWithCashback = [
        'NKSelectS',
        'NKSelectL',
        'NKSelectXL',
    ];
    var isOverTwentyYears = getAgeFromBirthYear(birthYear) > 20;
    var cashback = tariffsWithCashback.includes(tariff)
        ? cashbackForTariff({
            tariff: tariff,
            tariffBasePrice: basePrice,
            isOverTwentyYears: isOverTwentyYears,
            deductible: deductible,
        })
        : undefined;
    return __assign({ tariffName: tariff, addOns: {
            pricePerAddOn: pricePerAddOn,
            totalPrice: parseFloat(addOnsTotalPrice.toFixed(2)), // Round to 2 decimal places to avoid issues like 36.9 + 20.37 = 57.269999999999996
        }, tariff: {
            basePrice: basePrice,
            longTermCare: longTermCare,
            riskSurcharge: riskSurcharge,
            statutorySurcharge: statutorySurcharge,
            totalPrice: totalPrice,
        }, contributions: __assign({ employer: tariffContributions.employerContributions, own: tariffContributions.ownContributions }, (includeUnusedEmployerContributions
            ? {
                unusedEmployerContributions: tariffContributions.unusedEmployerContributions,
            }
            : {})) }, { cashback: cashback });
};
